import './App.css';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';

import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';

import useWindowSize from './Pages/hook/useWindowSize';
import ErrorBoundary from './Pages/common/ErrorBoundary';
import Message from './Pages/common/message';
import Footer from './Pages/nav/footer';
import { checkSession } from './Pages/common/session';

import Splash from './Pages/splash';
import Login from './Pages/login';
import Password from './Pages/password';
import MainImport from './Pages/main';
import IssuanceImport from './Pages/issuance';
import Profile from './Pages/profile';
import Search from './Pages/search';
import Qr from './Pages/qr';

import Setting from './Pages/setting';
import Main_pre from './Pages/main_pre';
import CertificateManageImport from './Pages/CertificateManage';

// default/named import 혼동 시 대비: 모듈 객체면 .default 사용
const Main = typeof MainImport === 'function' ? MainImport : (MainImport?.default ?? MainImport);
const Issuance = typeof IssuanceImport === 'function' ? IssuanceImport : (IssuanceImport?.default ?? IssuanceImport);
const CertificateManage = typeof CertificateManageImport === 'function' ? CertificateManageImport : (CertificateManageImport?.default ?? CertificateManageImport);

// 앱 환경 감지 (RN WebView 등)
const isAppEnvironment = () => {
    if (typeof window === "undefined") return false;
    if (window.ReactNativeWebView) return true;
    if (window.isNativeApp || window.__MY_CERT_USER_DATA__) return true;
    const ua = navigator.userAgent || "";
    return /wv/.test(ua) || (/Android/i.test(ua) && /Version\//.test(ua));
};

// 보호된 라우트 컴포넌트
function ProtectedRoute({ children }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [isChecking, setIsChecking] = useState(true);
    const [hasSession, setHasSession] = useState(false);

    useEffect(() => {
        const verifySession = async () => {
            // 📱 앱(RN WebView) 환경에서는 RN에서 로그인/세션을 관리하므로
            // 프론트 보호 라우트에서 추가로 세션을 검사하지 않고 통과시킨다.
            if (isAppEnvironment()) {
                console.log('📱 [ProtectedRoute] 앱 환경 - 세션 검사 생략, 바로 통과');
                setHasSession(true);
                setIsChecking(false);
                return;
            }

            const sessionResult = await checkSession();
            setHasSession(sessionResult.isValid);
            setIsChecking(false);

            if (!sessionResult.isValid) {
                // 세션이 없으면 로그인 페이지로 리다이렉트
                console.log('🔒 보호된 페이지 접근 시도 - 세션 없음, 로그인 페이지로 이동');
                navigate('/login', {
                    replace: true,
                    state: { from: location.pathname }
                });
            }
        };

        verifySession();
    }, [navigate, location.pathname]);

    if (isChecking) {
        return null; // 체크 중일 때는 아무것도 표시하지 않음
    }

    if (hasSession) {
        return children;
    }

    return null; // 세션이 없으면 null (리다이렉트 처리됨)
}

function App() {
    // 콘솔 에러 묵음 (운영 UI 깔끔)
    console.error = (message) => { };

    const location = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // 공개 페이지 (로그인 없이 접근 가능)
    const isLoginPage = location.pathname === "/" || location.pathname === "/login";
    const isPasswordPage = location.pathname === "/password";
    const isSearch = location.pathname === "/search";
    const isIssuance = location.pathname === "/issuance";

    // React Native WebView 메시지 리스너 (전역)
    useEffect(() => {
        if (!isAppEnvironment()) return;

        const handleRNMessage = (event) => {
            try {
                // React Native WebView는 event.data에 직접 JSON 문자열이 전달됨
                let data;
                if (typeof event.data === 'string') {
                    try {
                        data = JSON.parse(event.data);
                    } catch (e) {
                        data = event.data;
                    }
                } else {
                    data = event.data;
                }

                // 생체인식 관련 메시지는 password.js에서 처리하므로 여기서는 로그만
                if (data.type === 'REQUEST_BIOMETRIC_REGISTER' || 
                    data.type === 'BIOMETRIC_REGISTER_SUCCESS' ||
                    data.type === 'FINGERPRINT_SUCCESS' ||
                    data.type === 'FINGERPRINT_ERROR') {
                    console.log('📨 [App.js] 생체인식 관련 메시지 수신:', data.type);
                }
            } catch (e) {
                console.warn('⚠️ [App.js] 메시지 파싱 오류:', e, event);
            }
        };

        // document에 이벤트 리스너 추가
        document.addEventListener('message', handleRNMessage);
        window.addEventListener('message', handleRNMessage);

        return () => {
            document.removeEventListener('message', handleRNMessage);
            window.removeEventListener('message', handleRNMessage);
        };
    }, []);

    return (
        <div
            className="App"
            style={{
                minWidth: "100%"
            }}
        >
            <Message />
            <div className="App-section">
                <div className="App-sction-box">
                    {/* <ErrorBoundary> */}
                    <Routes>
                        {/* 공개 페이지 (로그인 불필요) */}
                        <Route path="/" element={<Splash />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/password" element={<Password />} />

                        {/* 보호된 페이지 (세션 필요) */}
                        <Route
                            path="/main"
                            element={
                                <ProtectedRoute>
                                    <Main />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/qr"
                            element={
                                <ProtectedRoute>
                                    <Qr />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/issuance"
                            element={
                                <ProtectedRoute>
                                    <Issuance />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/Profile"
                            element={
                                <ProtectedRoute>
                                    <Profile />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/search"
                            element={
                                <ProtectedRoute>
                                    <Search />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/setting"
                            element={
                                <ProtectedRoute>
                                    <Setting />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/certificate-manage"
                            element={
                                <ProtectedRoute>
                                    <CertificateManage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/main_pre"
                            element={
                                <ProtectedRoute>
                                    <Main_pre />
                                </ProtectedRoute>
                            }
                        />

                        <Route path="*" element={<Navigate to="/" />} />
                    </Routes>
                    {/* </ErrorBoundary> */}
                </div>
            </div>

            {!isLoginPage && !isPasswordPage && !isSearch && !isIssuance && (
                <>
                    <Footer />
                </>
            )}
        </div>
    );
}

export default App;
